import { useState, useEffect, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { usePageTitle } from '../hooks/usePageTitle';

type Responses = Record<string, Record<string, { value?: string | string[]; score?: number; follow_up?: string }>>;

const SECTION_TITLES = [
  { key: 'A', title: 'Where You Are Right Now', required: true },
  { key: 'B', title: 'What You\'re Hoping For', required: true },
  { key: 'C', title: 'What\'s In The Way', required: true },
  { key: 'D', title: 'Your Life Right Now', required: true },
  { key: 'E', title: 'How You Work Best', required: true },
  { key: 'F', title: 'A Bit More About You', required: false },
  { key: 'G', title: 'Anything Else', required: false },
];

export function SeedbedDiscovery() {
  usePageTitle('Seedbed Discovery Tool');
  const [params] = useSearchParams();
  const token = params.get('token');

  const [loading, setLoading] = useState(true);
  const [discoveryId, setDiscoveryId] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [candidateName, setCandidateName] = useState('');
  const [trainerName, setTrainerName] = useState('');
  const [alreadySubmitted, setAlreadySubmitted] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [responses, setResponses] = useState<Responses>({});
  const [optionalResponses, setOptionalResponses] = useState<Record<string, { value?: string | null }>>({});
  const [responseG, setResponseG] = useState('');
  const [sectionFOpened, setSectionFOpened] = useState(false);
  const [sectionFSkipped, setSectionFSkipped] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [initError, setInitError] = useState('');

  // Initialise — works with or without token
  useEffect(() => {
    const payload: Record<string, string> = {};
    if (token) payload.enrolment_token = token;

    api.post<{ discovery_id: string; session_token: string; candidate_name: string; trainer_name: string; already_submitted: boolean }>('/seedbed/discovery/start', payload)
      .then((data) => {
        setDiscoveryId(data.discovery_id);
        setSessionToken(data.session_token);
        setCandidateName(data.candidate_name || '');
        setTrainerName(data.trainer_name || '');
        setAlreadySubmitted(data.already_submitted);
      })
      .catch((err) => setInitError(err.message || 'Unable to start discovery.'))
      .finally(() => setLoading(false));
  }, [token]);

  // Auto-save on section change
  const autoSave = useCallback(async (section: string) => {
    if (!discoveryId || !sessionToken || !responses[section]) return;
    setSaving(true);
    try {
      await api.post(`/seedbed/discovery/${discoveryId}/save`, {
        session_token: sessionToken,
        section,
        responses: responses[section],
      });
    } catch { /* silent */ }
    setSaving(false);
  }, [discoveryId, sessionToken, responses]);

  // Auto-save on blur
  useEffect(() => {
    const handler = () => {
      const sectionKey = SECTION_TITLES[currentSection]?.key;
      if (sectionKey && responses[sectionKey]) autoSave(sectionKey);
    };
    window.addEventListener('blur', handler);
    return () => window.removeEventListener('blur', handler);
  }, [currentSection, responses, autoSave]);

  const updateResponse = (section: string, questionId: string, data: any) => {
    setResponses((prev) => ({
      ...prev,
      [section]: { ...prev[section], [questionId]: { ...prev[section]?.[questionId], ...data } },
    }));
  };

  const goNext = () => {
    const sectionKey = SECTION_TITLES[currentSection]?.key;
    if (sectionKey) autoSave(sectionKey);
    setCurrentSection((s) => Math.min(s + 1, SECTION_TITLES.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const goPrev = () => {
    setCurrentSection((s) => Math.max(s - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async () => {
    // Validate required
    const missing: string[] = [];
    if (!responses.A?.A1?.value) missing.push('A1');
    if (!responses.A?.A2?.value) missing.push('A2');
    if (responses.A?.A3?.score === undefined) missing.push('A3');
    if (!responses.B?.B1?.value) missing.push('B1');
    if (!responses.B?.B2?.value) missing.push('B2');
    if (!responses.B?.B3?.value) missing.push('B3');
    if (!responses.C?.C1?.value) missing.push('C1');
    if (!responses.C?.C2?.value) missing.push('C2');
    if (responses.D?.D1?.score === undefined) missing.push('D1');
    if (!responses.D?.D2?.value) missing.push('D2');
    if (!responses.E?.E1?.value || (Array.isArray(responses.E?.E1?.value) && responses.E.E1.value.length === 0)) missing.push('E1');
    if (!responses.E?.E2?.value) missing.push('E2');

    if (missing.length > 0) {
      setErrors(missing);
      // Navigate to first missing section
      const firstMissing = missing[0][0];
      const sectionIdx = SECTION_TITLES.findIndex((s) => s.key === firstMissing);
      if (sectionIdx >= 0) setCurrentSection(sectionIdx);
      return;
    }

    setSubmitting(true);
    try {
      await api.post(`/seedbed/discovery/${discoveryId}/submit`, {
        session_token: sessionToken,
        section_f_opened: sectionFOpened,
        responses_optional: sectionFSkipped ? null : optionalResponses,
        response_g: responseG || null,
      });
      setSubmitted(true);
    } catch (err) {
      setErrors(['SUBMIT_FAILED']);
    }
    setSubmitting(false);
  };

  // ─── Loading / Error states ───
  if (loading) {
    return <Shell><div className="text-center py-20"><p className="text-slate-400">Loading...</p></div></Shell>;
  }
  if (initError) {
    return <Shell><div className="text-center py-20"><p className="text-red-500 mb-2">Unable to load the Discovery Tool.</p><p className="text-sm text-slate-400">{initError}</p></div></Shell>;
  }
  if (alreadySubmitted || submitted) {
    return (
      <Shell>
        <div className="text-center py-16 sm:py-24 px-6">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
          </div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#1B4332] mb-3">
            Thank you{candidateName ? `, ${candidateName.split(' ')[0]}` : ''}
          </h1>
          <p className="text-slate-500 max-w-md mx-auto leading-relaxed mb-4">
            {trainerName
              ? `Your responses have been received. ${trainerName} will read them carefully before your first session — so when you meet, you won't be starting from scratch.`
              : 'Your responses have been received. When you are matched with a Seedbed trainer, they will read your discovery portrait before your first session.'}
          </p>
          <p className="text-sm text-slate-400">
            {trainerName
              ? "You don't need to do anything else before Session 1. We'll be in touch with your session details."
              : 'Interested in starting the Seedbed programme? Take the Readiness Assessment to find your path.'}
          </p>
          {!trainerName && (
            <a href="https://assessment.lifespark360.com/" className="inline-block mt-6 px-6 py-3 bg-[#1B4332] text-white rounded-xl font-semibold text-sm hover:bg-[#2D6A4F] transition-colors">
              Take Readiness Assessment
            </a>
          )}
        </div>
      </Shell>
    );
  }

  // ─── Form ───
  const section = SECTION_TITLES[currentSection];

  return (
    <Shell>
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Progress */}
        <div className="flex items-center justify-between mb-8">
          <p className="text-xs text-slate-500 font-medium">Section {currentSection + 1} of {SECTION_TITLES.length}</p>
          <p className="text-xs text-green-700 font-medium" aria-live="polite">{saving ? 'Saved' : ''}</p>
        </div>

        {/* Section header */}
        <div className="mb-8">
          <h2 className="font-display text-xl sm:text-2xl font-bold text-[#1B4332] mb-2">{section.title}</h2>
          {!section.required && <span className="inline-block text-xs bg-amber-100 text-amber-700 px-2 py-0.5 rounded font-medium">Optional</span>}
        </div>

        {/* Section content */}
        <div className="space-y-6">
          {section.key === 'A' && <SectionA responses={responses} updateResponse={updateResponse} errors={errors} />}
          {section.key === 'B' && <SectionB responses={responses} updateResponse={updateResponse} errors={errors} />}
          {section.key === 'C' && <SectionC responses={responses} updateResponse={updateResponse} errors={errors} />}
          {section.key === 'D' && <SectionD responses={responses} updateResponse={updateResponse} errors={errors} />}
          {section.key === 'E' && <SectionE responses={responses} updateResponse={updateResponse} errors={errors} />}
          {section.key === 'F' && (
            sectionFSkipped ? (
              <div className="text-center py-8">
                <p className="text-slate-500 mb-4">You've chosen to skip this section.</p>
                <button onClick={() => { setSectionFSkipped(false); setSectionFOpened(true); }} className="text-sm text-[#2D6A4F] font-medium hover:underline">Go back and answer</button>
              </div>
            ) : (
              <>
                {!sectionFOpened && (
                  <div className="mb-6">
                    <p className="text-sm text-slate-500 leading-relaxed mb-4">
                      This section is entirely optional. You can skip it completely, answer one question, or answer all of them. There's no right amount to share. What you do share will help your trainer understand your circumstances more fully. Only your trainer reads this section: it is not sent to the AI tool.
                    </p>
                    <button onClick={() => { setSectionFSkipped(true); goNext(); }} className="w-full py-3 bg-amber-50 border border-amber-200 text-amber-700 rounded-xl font-medium text-sm hover:bg-amber-100 transition-colors mb-4">
                      Skip this section
                    </button>
                    <button onClick={() => setSectionFOpened(true)} className="w-full py-3 bg-[#1B4332] text-white rounded-xl font-medium text-sm hover:bg-[#2D6A4F] transition-colors">
                      I'd like to share
                    </button>
                  </div>
                )}
                {sectionFOpened && <SectionF optionalResponses={optionalResponses} setOptionalResponses={setOptionalResponses} />}
              </>
            )
          )}
          {section.key === 'G' && <SectionG responseG={responseG} setResponseG={setResponseG} />}
        </div>

        {/* Navigation */}
        <div className="flex justify-between items-center mt-10 pt-6 border-t border-slate-100">
          {currentSection > 0 ? (
            <button onClick={goPrev} className="text-sm text-slate-500 hover:text-slate-700 font-medium transition-colors">&larr; Back</button>
          ) : <div />}

          {currentSection < SECTION_TITLES.length - 1 ? (
            <button onClick={goNext} className="px-6 py-2.5 bg-[#1B4332] text-white rounded-xl font-medium text-sm hover:bg-[#2D6A4F] transition-colors">
              Continue &rarr;
            </button>
          ) : (
            <button onClick={handleSubmit} disabled={submitting} className="px-8 py-3 bg-[#D4A017] text-[#1B4332] rounded-xl font-bold text-sm hover:bg-[#E8C547] transition-colors disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Submit'}
            </button>
          )}
        </div>

        <div role="alert" className="text-red-700 text-sm text-center mt-4">
          {errors.includes('SUBMIT_FAILED')
            ? 'Submission failed. Please try again.'
            : errors.length > 0
              ? `Please answer the ${errors.length === 1 ? 'highlighted question' : `${errors.length} highlighted questions`} before submitting.`
              : ''}
        </div>
      </div>
    </Shell>
  );
}

// ─── Shell: standalone layout (no nav, just the tool) ───
function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-[#FAFAF7]">
      <header className="bg-[#1B4332] text-white py-4">
        <div className="max-w-2xl mx-auto px-4 sm:px-6">
          <p className="font-display text-lg font-bold">Seedbed</p>
          <p className="text-white/80 text-xs">Discovery Tool</p>
        </div>
      </header>
      {children}
    </div>
  );
}

// ─── Shared components ───
// Each question is a fieldset: the legend names every control inside it (text
// inputs, sliders, radio and checkbox groups alike), and the guidance and error
// text are attached as descriptions so screen readers announce them too.
function QuestionCard({ id, label, guidance, error, children }: { id: string; label: string; guidance?: string; error?: boolean; children: React.ReactNode }) {
  const describedBy = [guidance ? `${id}-guidance` : '', error ? `${id}-error` : ''].filter(Boolean).join(' ') || undefined;
  return (
    <fieldset
      id={id}
      aria-describedby={describedBy}
      aria-invalid={error || undefined}
      className={`bg-white rounded-xl p-5 sm:p-6 border min-w-0 ${error ? 'border-red-300 ring-2 ring-red-100' : 'border-slate-100'}`}
    >
      <legend className="float-left w-full text-sm font-medium text-[#1A1A2E] mb-1">{label}</legend>
      <div className="clear-left">
        {guidance && <p id={`${id}-guidance`} className="text-xs text-slate-500 mb-3 leading-relaxed">{guidance}</p>}
        {children}
        {error && <p id={`${id}-error`} className="text-xs text-red-700 mt-2">This question helps your trainer prepare — even a rough answer is more useful than none.</p>}
      </div>
    </fieldset>
  );
}

function Textarea({ value, onChange, placeholder, optional }: { value: string; onChange: (v: string) => void; placeholder?: string; optional?: boolean }) {
  return (
    <div>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={4}
        className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent outline-none resize-none leading-relaxed"
      />
      {optional && <p className="text-[11px] text-slate-500 mt-1">Optional</p>}
    </div>
  );
}

// `recorded` is false until the learner's answer has been stored. The slider
// shows a starting value, so we store that value as soon as the question
// appears: what the person sees is what gets submitted, and an untouched slider
// no longer blocks submission.
function Slider({ value, recorded, onChange, min, max, labels }: { value: number; recorded: boolean; onChange: (v: number) => void; min: number; max: number; labels?: Record<number, string> }) {
  useEffect(() => {
    if (!recorded) onChange(value);
    // Only re-run when the answer's recorded state changes, not on every value tick.
  }, [recorded]);

  const minLabel = labels?.[min] || String(min);
  const maxLabel = labels?.[max] || String(max);
  return (
    <div>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        aria-valuetext={`${value} out of ${max}${value === min ? ` (${minLabel})` : value === max ? ` (${maxLabel})` : ''}`}
        className="w-full accent-[#1B4332]"
      />
      <div className="flex justify-between gap-3 text-[11px] text-slate-500 mt-1" aria-hidden="true">
        <span>{minLabel}</span>
        <span className="text-lg font-bold text-[#1B4332]">{value}</span>
        <span className="text-right">{maxLabel}</span>
      </div>
    </div>
  );
}

// Native inputs (visually hidden) carry the state, so the options work with a
// mouse, a keyboard (arrow keys / space) and a screen reader.
function RadioGroup({ name, options, value, onChange }: { name: string; options: { label: string; value: string }[]; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-2">
      {options.map((opt) => (
        <label key={opt.value} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#2D6A4F] ${value === opt.value ? 'border-[#2D6A4F] bg-[#2D6A4F]/5' : 'border-slate-200 hover:border-slate-300'}`}>
          <input
            type="radio"
            name={name}
            value={opt.value}
            checked={value === opt.value}
            onChange={() => onChange(opt.value)}
            className="sr-only"
          />
          <span aria-hidden="true" className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${value === opt.value ? 'border-[#2D6A4F]' : 'border-slate-400'}`}>
            {value === opt.value && <span className="w-2 h-2 rounded-full bg-[#2D6A4F]" />}
          </span>
          <span className="text-sm text-[#1A1A2E]">{opt.label}</span>
        </label>
      ))}
    </div>
  );
}

function CheckboxGroup({ name, options, value, onChange }: { name: string; options: string[]; value: string[]; onChange: (v: string[]) => void }) {
  const toggle = (opt: string) => {
    onChange(value.includes(opt) ? value.filter((v) => v !== opt) : [...value, opt]);
  };
  return (
    <div className="space-y-2">
      {options.map((opt) => (
        <label key={opt} className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-[#2D6A4F] ${value.includes(opt) ? 'border-[#2D6A4F] bg-[#2D6A4F]/5' : 'border-slate-200 hover:border-slate-300'}`}>
          <input
            type="checkbox"
            name={name}
            value={opt}
            checked={value.includes(opt)}
            onChange={() => toggle(opt)}
            className="sr-only"
          />
          <span aria-hidden="true" className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 ${value.includes(opt) ? 'border-[#2D6A4F] bg-[#2D6A4F]' : 'border-slate-400'}`}>
            {value.includes(opt) && <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>}
          </span>
          <span className="text-sm text-[#1A1A2E]">{opt}</span>
        </label>
      ))}
    </div>
  );
}

// ─── Sections ───
interface SectionProps { responses: Responses; updateResponse: (section: string, qId: string, data: any) => void; errors: string[] }

function SectionA({ responses, updateResponse, errors }: SectionProps) {
  return (
    <>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        Before your first Seedbed session, we'd like to understand a little about where you are right now. There are no right answers here — only honest ones. Your trainer will read your responses before you meet, so the more openly you can share, the more useful that first session will be. This takes about 10–15 minutes.
      </p>
      <div className="bg-white rounded-xl p-4 border border-slate-200 mb-4">
        <p className="text-sm font-medium text-[#1A1A2E] mb-1">How we use your answers</p>
        <p className="text-sm text-slate-600 leading-relaxed">
          When you submit, we use an AI tool to turn your answers into a short summary that helps your trainer prepare for your first session. Your trainer can also read your own answers. The optional section about your personal circumstances is never sent to the AI tool: only your trainer reads it.
        </p>
      </div>
      <QuestionCard id="A1" label="In one sentence, how would you describe where you are in your career or working life right now?" error={errors.includes('A1')}>
        <input type="text" value={responses.A?.A1?.value as string || ''} onChange={(e) => updateResponse('A', 'A1', { value: e.target.value })} placeholder="e.g. I feel stuck and not sure what direction to take" className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent outline-none" />
      </QuestionCard>
      <QuestionCard id="A2" label="What's happened recently — or built up over time — that led you to reach out for support?" guidance="Take your time with this one. It doesn't have to be a tidy answer." error={errors.includes('A2')}>
        <Textarea value={responses.A?.A2?.value as string || ''} onChange={(v) => updateResponse('A', 'A2', { value: v })} />
      </QuestionCard>
      <QuestionCard id="A3" label="On a scale of 1 to 10, how would you describe your current energy and headspace for doing this kind of work?" guidance="After selecting a score, you can optionally add a few words about what's most affecting it." error={errors.includes('A3')}>
        <Slider value={responses.A?.A3?.score ?? 5} recorded={responses.A?.A3?.score !== undefined} onChange={(v) => updateResponse('A', 'A3', { score: v })} min={1} max={10} labels={{ 1: 'Completely depleted', 10: 'Ready and motivated' }} />
        <input type="text" value={responses.A?.A3?.follow_up || ''} onChange={(e) => updateResponse('A', 'A3', { follow_up: e.target.value })} placeholder="What's most affecting your energy right now? (optional)" className="w-full px-4 py-2 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent outline-none mt-3" />
      </QuestionCard>
    </>
  );
}

function SectionB({ responses, updateResponse, errors }: SectionProps) {
  return (
    <>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        This section is about what you want from Seedbed — not in a grand life-plan sense, but in a grounded, realistic sense. What would make this worthwhile for you?
      </p>
      <QuestionCard id="B1" label="If something genuinely shifted during these eight weeks, what would be different for you?" guidance="It could be a feeling, a decision, a habit, a conversation you've been putting off — anything." error={errors.includes('B1')}>
        <Textarea value={responses.B?.B1?.value as string || ''} onChange={(v) => updateResponse('B', 'B1', { value: v })} />
      </QuestionCard>
      <QuestionCard id="B2" label="What's the one thing you most want to understand about yourself or your direction by the end of this programme?" error={errors.includes('B2')}>
        <input type="text" value={responses.B?.B2?.value as string || ''} onChange={(e) => updateResponse('B', 'B2', { value: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-[#2D6A4F] focus:border-transparent outline-none" />
      </QuestionCard>
      <QuestionCard id="B3" label="Six months from now, what would tell you that this time was well spent?" error={errors.includes('B3')}>
        <Textarea value={responses.B?.B3?.value as string || ''} onChange={(v) => updateResponse('B', 'B3', { value: v })} />
      </QuestionCard>
    </>
  );
}

function SectionC({ responses, updateResponse, errors }: SectionProps) {
  return (
    <>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        This is often the most important section. We're not asking about your weaknesses — we're asking about the specific obstacle that keeps showing up when you try to move forward.
      </p>
      <QuestionCard id="C1" label="What's the ONE thing that most consistently gets in the way when you try to make changes or move forward?" guidance="This might be internal — a belief, a pattern, a fear — or external, like time, circumstances, or another person. Whatever it is, name it as clearly as you can. There's no wrong answer." error={errors.includes('C1')}>
        <Textarea value={responses.C?.C1?.value as string || ''} onChange={(v) => updateResponse('C', 'C1', { value: v })} />
      </QuestionCard>
      <QuestionCard id="C2" label="How long has this been in the way for you?" error={errors.includes('C2')}>
        <RadioGroup
          name="C2"
          options={[
            { label: 'Less than 6 months', value: 'less_6m' },
            { label: 'Around a year', value: 'around_year' },
            { label: 'A few years', value: 'few_years' },
            { label: 'As long as I can remember', value: 'always' },
            { label: 'I\'m not sure', value: 'unsure' },
          ]}
          value={responses.C?.C2?.value as string || ''}
          onChange={(v) => updateResponse('C', 'C2', { value: v })}
        />
      </QuestionCard>
    </>
  );
}

function SectionD({ responses, updateResponse, errors }: SectionProps) {
  return (
    <>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        Seedbed works best when we pace it to match your real life — not the life you wish you had. These questions help us get that right.
      </p>
      <QuestionCard id="D1" label="How full is your life right now?" error={errors.includes('D1')}>
        <Slider value={responses.D?.D1?.score ?? 5} recorded={responses.D?.D1?.score !== undefined} onChange={(v) => updateResponse('D', 'D1', { score: v })} min={1} max={10} labels={{ 1: 'Quiet — space and bandwidth', 10: 'Overloaded — running on fumes' }} />
      </QuestionCard>
      <QuestionCard id="D2" label="Is there a regular time each week — even 30 minutes — you could reliably protect for this programme?" error={errors.includes('D2')}>
        <RadioGroup
          name="D2"
          options={[
            { label: 'Yes — I already have a time in mind', value: 'yes' },
            { label: 'Probably — I\'d need to plan it', value: 'probably' },
            { label: 'Not sure — depends on the week', value: 'unsure' },
            { label: 'Honestly no — my schedule is very unpredictable right now', value: 'no' },
          ]}
          value={responses.D?.D2?.value as string || ''}
          onChange={(v) => updateResponse('D', 'D2', { value: v })}
        />
      </QuestionCard>
      <QuestionCard id="D3" label="Is there anything significant going on in your life right now that your trainer should be aware of before your first session?" guidance="This is entirely optional. Only share what you're comfortable sharing.">
        <Textarea value={responses.D?.D3?.value as string || ''} onChange={(v) => updateResponse('D', 'D3', { value: v })} optional />
      </QuestionCard>
    </>
  );
}

function SectionE({ responses, updateResponse, errors }: SectionProps) {
  return (
    <>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        These questions help your trainer understand how you think, how you receive support, and how to make your sessions as useful as possible.
      </p>
      <QuestionCard id="E1" label="When you're stuck or overwhelmed, what tends to help you most?" guidance="Select all that apply." error={errors.includes('E1')}>
        <CheckboxGroup
          name="E1"
          options={['Talking it through', 'Time alone to reflect', 'A concrete task or next step', 'Being challenged or gently pushed', 'Permission to do nothing for a bit', 'I genuinely don\'t know yet']}
          value={(responses.E?.E1?.value as string[]) || []}
          onChange={(v) => updateResponse('E', 'E1', { value: v })}
        />
      </QuestionCard>
      <QuestionCard id="E2" label="When it comes to direct feedback, you prefer:" error={errors.includes('E2')}>
        <RadioGroup
          name="E2"
          options={[
            { label: 'Direct and clear, even when uncomfortable', value: 'direct' },
            { label: 'Gentle and carefully framed — I need to feel safe first', value: 'gentle' },
            { label: 'A mix depending on the topic', value: 'mix' },
            { label: 'I\'m not sure — happy for my trainer to read the room', value: 'unsure' },
          ]}
          value={responses.E?.E2?.value as string || ''}
          onChange={(v) => updateResponse('E', 'E2', { value: v })}
        />
      </QuestionCard>
      <QuestionCard id="E3" label="When you try to start something new, what usually gets in the way before it sticks?" guidance="You don't have to have a neat answer. Even 'I don't know — I just seem to stop' is useful.">
        <Textarea value={responses.E?.E3?.value as string || ''} onChange={(v) => updateResponse('E', 'E3', { value: v })} optional />
      </QuestionCard>
      <QuestionCard id="E4" label="Is there a time of day when you tend to think most clearly?">
        <RadioGroup
          name="E4"
          options={[
            { label: 'Morning', value: 'morning' },
            { label: 'Midday', value: 'midday' },
            { label: 'Afternoon', value: 'afternoon' },
            { label: 'Evening', value: 'evening' },
            { label: 'It varies', value: 'varies' },
            { label: 'I genuinely don\'t know', value: 'unsure' },
          ]}
          value={responses.E?.E4?.value as string || ''}
          onChange={(v) => updateResponse('E', 'E4', { value: v })}
        />
      </QuestionCard>
    </>
  );
}

function SectionF({ optionalResponses, setOptionalResponses }: { optionalResponses: Record<string, { value?: string | null }>; setOptionalResponses: (v: any) => void }) {
  const update = (key: string, value: string) => setOptionalResponses((prev: any) => ({ ...prev, [key]: { value: value || null } }));
  return (
    <>
      <QuestionCard id="F1" label="Is there anything in your personal circumstances right now — health, family, finances, relationships, caring responsibilities — that might affect your capacity during this programme?" guidance="Only share what you're comfortable sharing. Your trainer will keep what you tell us confidential. The one exception is if we're worried about your safety or someone else's: then we may need to share information to keep people safe, and we'll talk to you first wherever we can.">
        <Textarea value={optionalResponses.F1?.value || ''} onChange={(v) => update('F1', v)} optional />
      </QuestionCard>
      <QuestionCard id="F2" label="Is there anyone in your life who might be affected by you doing this work — positively or negatively?" guidance="Sometimes the people closest to us have strong reactions when we start changing. If that's relevant, your trainer would find it useful to know.">
        <Textarea value={optionalResponses.F2?.value || ''} onChange={(v) => update('F2', v)} optional />
      </QuestionCard>
      <QuestionCard id="F3" label="Are there any topics, areas, or approaches that you'd prefer your trainer to handle with particular sensitivity or care?">
        <Textarea value={optionalResponses.F3?.value || ''} onChange={(v) => update('F3', v)} optional />
      </QuestionCard>
    </>
  );
}

function SectionG({ responseG, setResponseG }: { responseG: string; setResponseG: (v: string) => void }) {
  return (
    <>
      <p className="text-sm text-slate-500 leading-relaxed mb-4">
        This is a free space. Use it however you want.
      </p>
      <QuestionCard id="G1" label="Is there anything else you'd like your trainer to know before your first session — anything at all that doesn't fit the questions above?">
        <Textarea value={responseG} onChange={setResponseG} optional />
      </QuestionCard>
    </>
  );
}
